import { getElectronAPI } from "@/plugins/desktop";
import ApiOfflineService from "./ApiOfflineService";
import BalancaEtiquetaService from "./BalancaEtiquetaService";

const STORAGE_KEY = "configuracoes_gerais_pdv";

const isTrue = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const DEFAULTS = Object.freeze({
  usar_codigo_balanca: false,
  balanca_etiqueta_tipo: "peso",
  balanca_tamanho_codigo_produto: 4,
  balanca_casas_decimais: 3,
  solicita_senha_adm: false,
  abertura_caixa: true,
  fechamento_as_cegas: false,
});

function unwrap(payload) {
  const value = payload?.data ?? payload ?? {};
  return Array.isArray(value) ? (value[0] || {}) : value;
}

function normalizar(config = {}) {
  const merged = { ...DEFAULTS, ...unwrap(config) };
  const aberturaCaixa = isTrue(merged.abertura_caixa);
  return {
    ...merged,
    usar_codigo_balanca: isTrue(merged.usar_codigo_balanca),
    solicita_senha_adm: isTrue(merged.solicita_senha_adm),
    abertura_caixa: aberturaCaixa,
    fechamento_as_cegas: aberturaCaixa && isTrue(merged.fechamento_as_cegas),
    balanca_tamanho_codigo_produto: Number(merged.balanca_tamanho_codigo_produto) || 4,
    balanca_casas_decimais: Number(merged.balanca_casas_decimais) || 3,
  };
}

async function lerConfiguracaoSalva() {
  try {
    const raw = await getElectronAPI()?.config?.get(STORAGE_KEY);
    const config = raw ? JSON.parse(raw) : null;
    return config && typeof config === "object" && !Array.isArray(config)
      && Object.keys(config).length ? normalizar(config) : null;
  } catch (_) {
    return null;
  }
}

const ConfiguracaoGeralPdvDesktopService = {
  defaults: DEFAULTS,
  normalizar,

  async carregarLocal() {
    return (await lerConfiguracaoSalva()) || normalizar();
  },

  async carregarAtualizada() {
    const local = await this.carregarLocal();
    const api = getElectronAPI();
    const token = api?.config ? await api.config.get("api_token").catch(() => null) : null;
    if (!token || String(token).startsWith("offline:")
      || (typeof navigator !== "undefined" && navigator.onLine === false)) {
      return local;
    }

    // Uma conexão indisponível não deve impedir o uso do caixa offline.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      return await this.sincronizar({ signal: controller.signal });
    } catch (_) {
      return local;
    } finally {
      clearTimeout(timeout);
    }
  },

  async salvarLocal(config = {}, { somenteSeAusente = false } = {}) {
    if (somenteSeAusente) {
      const salva = await lerConfiguracaoSalva();
      if (salva) return salva;
    }
    const normalizada = normalizar(config);
    const api = getElectronAPI();
    if (api?.config) {
      await api.config.set(STORAGE_KEY, JSON.stringify(normalizada));
    }

    try {
      const balancaLocal = await BalancaEtiquetaService.carregarLocal();
      await BalancaEtiquetaService.salvarLocal(
        BalancaEtiquetaService.mesclar(balancaLocal, normalizada)
      );
    } catch (e) {
      console.warn("[Configurações PDV] Falha ao atualizar etiqueta de balança:", e?.message || e);
    }
    return normalizada;
  },

  async sincronizar({ signal } = {}) {
    const response = await ApiOfflineService.post("configuracoesgerais/find", {}, { signal });
    const config = unwrap(response);
    if (response?.status === false || !config || typeof config !== "object"
      || !Object.keys(DEFAULTS).some((key) => config[key] !== undefined)) {
      throw new Error("Não foi possível obter as configurações gerais do PDV.");
    }
    return this.salvarLocal(response);
  },
};

export default ConfiguracaoGeralPdvDesktopService;
